// Search and faceted filtering for the voice catalog, shared by the voice
// picker (client) and the voice list APIs (server). Pure, no imports beyond
// types, so it runs anywhere and is unit-tested by
// scripts/test-voice-catalog.cjs.
import type { Voice } from 'types';

export type VoiceFacet =
  | 'language'
  | 'accent'
  | 'gender'
  | 'age'
  | 'useCase'
  | 'tone';

export const VOICE_FACETS: VoiceFacet[] = [
  'language',
  'accent',
  'gender',
  'age',
  'useCase',
  'tone',
];

export interface VoiceFilterState {
  search: string;
  language: string;
  accent: string;
  gender: string;
  age: string;
  useCase: string;
  tone: string;
  onlyExpressive: boolean;
  onlyFavorites: boolean;
}

export const EMPTY_VOICE_FILTERS: VoiceFilterState = {
  search: '',
  language: '',
  accent: '',
  gender: '',
  age: '',
  useCase: '',
  tone: '',
  onlyExpressive: false,
  onlyFavorites: false,
};

export interface VoiceFilterContext {
  favorites?: ReadonlySet<string>;
  isExpressive?: (voice: Voice) => boolean;
  /**
   * Text a voice is searched against, from buildVoiceSearchText. Callers
   * memoize it per voice; without it the name, description, language and
   * accent are used.
   */
  searchText?: (voice: Voice) => string;
}

/** The languages the voice is listed under; the first is its own. */
export const voiceLanguages = (voice: Voice): string[] => {
  if (!voice.language) return [];
  return Array.isArray(voice.language) ? voice.language : [voice.language];
};

/**
 * What the language filter matches: the voice's own languages plus those
 * ElevenLabs verified it for (`other_languages`), as ElevenLabs' own voice
 * picker does. Search still uses only the voice's own languages.
 */
export const voiceFilterLanguages = (voice: Voice): string[] => {
  const own = voiceLanguages(voice);
  const others = (voice.other_languages || []).filter(
    (language) => !own.includes(language),
  );
  return others.length ? [...own, ...others] : own;
};

/**
 * `language` is the selected language filter, if any. A voice's accent
 * belongs to its own languages: one that is only verified for the selected
 * language has no accent in it ("Spanish" + "American" must not return
 * American English voices that also speak Spanish).
 */
export const voiceFacetValues = (
  voice: Voice,
  facet: VoiceFacet,
  language = '',
): string[] => {
  switch (facet) {
    case 'language':
      return voiceFilterLanguages(voice);
    case 'accent':
      if (!voice.accent) return [];
      if (language && !voiceLanguages(voice).includes(language)) return [];
      return [voice.accent];
    case 'gender':
      return voice.gender ? [voice.gender] : [];
    case 'age':
      return voice.age ? [voice.age] : [];
    case 'useCase':
      return voice.use_case ? [voice.use_case] : [];
    case 'tone':
      return voice.tone ? [voice.tone] : [];
  }
};

/** Lower case without diacritics, so "Zoë" is found by "zoe". */
export const normalizeSearchText = (value: string) =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const voiceSearchTokens = (query: string): string[] =>
  normalizeSearchText(query)
    .split(/[\s,]+/)
    .map((token) => token.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ''))
    .filter(Boolean);

/**
 * Words a voice can be found by: its name, description, languages, accent
 * and style tags, plus `extraTerms` (the picker passes the gender, age, use
 * case and tone labels in English and in the user's language). Returned with
 * a leading space so a search token matches the start of any word.
 */
export const buildVoiceSearchText = (
  voice: Voice,
  extraTerms: Array<string | null | undefined> = [],
): string => {
  const words = normalizeSearchText(
    [
      voice.name,
      voice.description || '',
      voice.accent || '',
      ...voiceLanguages(voice),
      ...(voice.styles || []),
      ...extraTerms.filter(Boolean),
    ].join(' '),
  )
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  return ` ${words.join(' ')}`;
};

/**
 * Every token must match: the start of a word in the voice's search text
 * ("brit" finds "British", "male" does not find "female"), or anywhere in
 * the name.
 */
const matchesTokens = (voice: Voice, tokens: string[], text: string) => {
  if (tokens.length === 0) return true;
  const name = normalizeSearchText(voice.name || '');
  return tokens.every(
    (token) => text.includes(` ${token}`) || name.includes(token),
  );
};

export interface VoiceMatcher {
  /** Passes search, the Audio tags switch and the favourites switch. */
  passesBase: (voice: Voice) => boolean;
  /** Passes every filter; `ignore` skips one facet (for facet counts). */
  matches: (voice: Voice, ignore?: VoiceFacet) => boolean;
}

export const createVoiceMatcher = (
  filters: VoiceFilterState,
  ctx: VoiceFilterContext = {},
): VoiceMatcher => {
  const tokens = voiceSearchTokens(filters.search);
  const searchText =
    ctx.searchText || ((voice: Voice) => buildVoiceSearchText(voice));

  const passesBase = (voice: Voice) => {
    if (filters.onlyExpressive && !ctx.isExpressive?.(voice)) return false;
    if (filters.onlyFavorites && !ctx.favorites?.has(voice.voice_id)) {
      return false;
    }
    return matchesTokens(voice, tokens, tokens.length ? searchText(voice) : '');
  };

  const passesFacet = (voice: Voice, facet: VoiceFacet) => {
    const wanted = filters[facet];
    return (
      !wanted ||
      voiceFacetValues(voice, facet, filters.language).includes(wanted)
    );
  };

  return {
    passesBase,
    matches: (voice, ignore) =>
      passesBase(voice) &&
      VOICE_FACETS.every(
        (facet) => facet === ignore || passesFacet(voice, facet),
      ),
  };
};

/**
 * With a language selected, voices listed under it come before those only
 * verified for it (ElevenLabs' order); catalog order is kept within each.
 */
export const filterVoices = <T extends Voice>(
  voices: T[],
  filters: VoiceFilterState,
  ctx: VoiceFilterContext = {},
): T[] => {
  const matcher = createVoiceMatcher(filters, ctx);
  const matches = voices.filter((voice) => matcher.matches(voice));
  if (!filters.language) return matches;
  const own: T[] = [];
  const verified: T[] = [];
  for (const voice of matches) {
    (voiceLanguages(voice).includes(filters.language) ? own : verified).push(
      voice,
    );
  }
  return verified.length ? [...own, ...verified] : matches;
};

export type VoiceFacetCounts = Record<VoiceFacet, Map<string, number>>;

/**
 * For each facet, how many voices each value would show, counting the voices
 * that match every OTHER filter (the usual faceted-search rule, so picking
 * "Female" still shows how many male voices the other filters allow).
 */
export const countVoiceFacets = (
  voices: Voice[],
  filters: VoiceFilterState,
  ctx: VoiceFilterContext = {},
): VoiceFacetCounts => {
  const counts = Object.fromEntries(
    VOICE_FACETS.map((facet) => [facet, new Map<string, number>()]),
  ) as VoiceFacetCounts;
  const matcher = createVoiceMatcher(filters, ctx);

  for (const voice of voices) {
    if (!matcher.passesBase(voice)) continue;
    // A voice that fails no facet counts everywhere; one that fails exactly
    // one facet counts only toward that facet's values.
    let failed: VoiceFacet | null = null;
    let failures = 0;
    for (const facet of VOICE_FACETS) {
      const wanted = filters[facet];
      if (
        wanted &&
        !voiceFacetValues(voice, facet, filters.language).includes(wanted)
      ) {
        failures += 1;
        failed = facet;
        if (failures > 1) break;
      }
    }
    if (failures > 1) continue;
    for (const facet of VOICE_FACETS) {
      if (failed && facet !== failed) continue;
      for (const value of voiceFacetValues(voice, facet, filters.language)) {
        counts[facet].set(value, (counts[facet].get(value) || 0) + 1);
      }
    }
  }
  return counts;
};

/** Facet filters and switches in use (search is shown separately). */
export const activeVoiceFilterCount = (filters: VoiceFilterState) =>
  VOICE_FACETS.filter((facet) => Boolean(filters[facet])).length +
  (filters.onlyExpressive ? 1 : 0) +
  (filters.onlyFavorites ? 1 : 0);

/**
 * Favourites first, otherwise the order is kept. With a language selected,
 * voices listed under it stay ahead of those only verified for it, and
 * favourites go first within each of the two groups.
 */
export const sortFavoritesFirst = <T extends Voice>(
  voices: T[],
  favorites: ReadonlySet<string>,
  language = '',
): T[] => {
  if (favorites.size === 0) return voices;
  // own favourites, own others, verified favourites, verified others
  const groups: T[][] = [[], [], [], []];
  for (const voice of voices) {
    const verifiedOnly =
      Boolean(language) && !voiceLanguages(voice).includes(language);
    groups[
      (verifiedOnly ? 2 : 0) + (favorites.has(voice.voice_id) ? 0 : 1)
    ].push(voice);
  }
  return groups.flat();
};
